import React, { useState, useRef, useEffect } from 'react';
import './TryOnForm.css';
import { resolveApiBase } from '../utils/apiBase';

const API_BASE = resolveApiBase();

// SVG Icons for password visibility toggle
const EyeOpenIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

function TryOnForm() {
  const [avatar, setAvatar] = useState(null); // File if user uploaded
  const [avatarSource, setAvatarSource] = useState(null); // { type: 'file'|'library', name, url }
  const [avatarLibOpen, setAvatarLibOpen] = useState(false);
  // Built-in avatar JPEG files served from public/avatars.
  // Added male (avatarM*) and female (avatarF*) variants.
  const avatarLibrary = [
    { name: 'Male Avatar 1', file: '/avatars/avatarM1.jpg', gender: 'male' },
    { name: 'Male Avatar 2', file: '/avatars/avatarM2.jpg', gender: 'male' },
    { name: 'Male Avatar 3', file: '/avatars/avatarM3.jpg', gender: 'male' },
    { name: 'Female Avatar 1', file: '/avatars/avatarF1.jpg', gender: 'female' },
    { name: 'Female Avatar 2', file: '/avatars/avatarF2.jpg', gender: 'female' },
    { name: 'Female Avatar 3', file: '/avatars/avatarF3.jpg', gender: 'female' },
  ];
  const [avatarGenderFilter, setAvatarGenderFilter] = useState('all'); // 'male' | 'female' | 'all'
  const [item, setItem] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('Idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [syncingCredits, setSyncingCredits] = useState(false);
  const [proxyUrl, setProxyUrl] = useState(null);
  const [shareUrlCache, setShareUrlCache] = useState(null);
  const [creditsInfo, setCreditsInfo] = useState({ remaining: null, monthlyUsed: null, monthlyLimit: null, debug: false, isUser: false, userOffer: null });
  const [userInfo, setUserInfo] = useState(null);
  const broadcastCredits = (info) => {
    try {
      if (typeof window === 'undefined') return;
      window.dispatchEvent(new CustomEvent('credits-update', { detail: info }));
    } catch (_) {
      // ignore
    }
  };
  const abortRef = useRef(null);
  // Inline email OTP sign-in (shown only when anonymous credits exhausted)
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpStatus, setOtpStatus] = useState('');
  const [otpError, setOtpError] = useState('');
  const otpEmailRef = useRef(null);
  const otpCodeRef = useRef(null);
  // Password sign-in and reset
  const [authMode, setAuthMode] = useState('otp'); // 'otp' | 'password' | 'reset'
  const [pwd, setPwd] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [showForgotAfterFail, setShowForgotAfterFail] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  const avatarPreviewRef = useRef(null);
  const itemPreviewRef = useRef(null);
  // Separate inputs so mobile can choose Camera or Gallery explicitly
  const avatarCamInputRef = useRef(null);
  const avatarFileInputRef = useRef(null);
  const garmentCamInputRef = useRef(null);
  const garmentFileInputRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Prefer showing Camera vs Gallery on mobile; a single picker on desktop
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    try {
      const coarse = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer:coarse)').matches;
      const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
      const mobi = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
      setIsMobile(Boolean(coarse || mobi));
    } catch(_) { setIsMobile(false); }
  }, []);

  const [garmentSource, setGarmentSource] = useState(null); // { type: 'file'|'library', name, url }
  const [garmentLibOpen, setGarmentLibOpen] = useState(false);
  const [garmentGenderFilter, setGarmentGenderFilter] = useState('all'); // 'male' | 'female' | 'all'
  // Fetch free credits info from backend
  const fetchCredits = async () => {
    try {
      const base = API_BASE || window.location.origin;
      const url = new URL('/trial/credits', base);
      const resp = await fetch(url.toString(), { credentials: 'include' });
      if (!resp.ok) return;
      const ct = (resp.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        // Likely SPA HTML from Nginx catch-all; skip to allow fallback timer to handle
        return;
      }
      const data = await resp.json();
      const next = {
        remaining: typeof data.credits_remaining === 'number' ? data.credits_remaining : null,
        monthlyUsed: typeof data.monthly_used === 'number' ? data.monthly_used : null,
        monthlyLimit: typeof data.monthly_limit === 'number' ? data.monthly_limit : null,
        debug: !!data.debug,
        isUser: !!data.is_user,
        userOffer: typeof data.user_free_credit_offer === 'number' ? data.user_free_credit_offer : null,
      };
      setCreditsInfo(next);
      broadcastCredits(next);
    } catch (_) {
      // ignore for UI friendliness
    }
  };

  const fetchMe = async () => {
    try {
      const base = API_BASE || window.location.origin;
      const url = new URL('/me', base);
      const resp = await fetch(url.toString(), { credentials: 'include' });
      if (!resp.ok) {
        setUserInfo(null);
        return;
      }
      const data = await resp.json();
      setUserInfo(data);
    } catch (_) {
      setUserInfo(null);
    }
  };

  useEffect(() => {
    // Fetch from backend (authoritative source)
    fetchCredits();
    fetchMe();
    const handler = () => {
      fetchCredits();
      fetchMe();
    };
    window.addEventListener('auth-change', handler);
    return () => window.removeEventListener('auth-change', handler);
  }, []);
  const garmentScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Expanded garment library with gender-specific assets (garmentM1..M5, garmentF1..F5)
  const garmentLibrary = [
    { name: 'Male Garment 1', file: '/garments/garmentM1.jpg', gender: 'male' },
    { name: 'Male Garment 2', file: '/garments/garmentM2.jpg', gender: 'male' },
    { name: 'Male Garment 3', file: '/garments/garmentM3.jpg', gender: 'male' },
    { name: 'Male Garment 4', file: '/garments/garmentM4.jpg', gender: 'male' },
    { name: 'Male Garment 5', file: '/garments/garmentM5.jpg', gender: 'male' },
    { name: 'Female Garment 1', file: '/garments/garmentF1.jpg', gender: 'female' },
    { name: 'Female Garment 2', file: '/garments/garmentF2.jpg', gender: 'female' },
    { name: 'Female Garment 3', file: '/garments/garmentF3.jpg', gender: 'female' },
    { name: 'Female Garment 4', file: '/garments/garmentF4.jpg', gender: 'female' },
    { name: 'Female Garment 5', file: '/garments/garmentF5.jpg', gender: 'female' },
    { name: 'Female Garment 6', file: '/garments/garmentF6.jpg', gender: 'female' },
    { name: 'Female Garment 7', file: '/garments/garmentF7.jpg', gender: 'female' },
    { name: 'Female Garment 8', file: '/garments/garmentF8.jpg', gender: 'female' },
    { name: 'Female Garment 9', file: '/garments/garmentF9.jpg', gender: 'female' },
    { name: 'Female Garment 10', file: '/garments/garmentF10.jpg', gender: 'female' },
  ];
  // Derive selected avatar gender (library picks only). If user uploaded a custom avatar, show all garments.
  const selectedAvatarGender = avatarSource?.type === 'library'
    ? avatarLibrary.find(a => a.file === avatarSource.url)?.gender || null
    : null;

  // Debug mode: show full editable prompt. Normal mode: show single-line, disabled preview.
  const DEBUG_ENV = process.env.REACT_APP_DEBUG_MODE === 'true';
  const isDebug = React.useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const dv = q.get('debug');
      if (dv !== null) {
        return ['1','true','yes','on'].includes(String(dv).toLowerCase());
      }
    } catch (_) { /* ignore */ }
    return DEBUG_ENV;
  }, [DEBUG_ENV]);

  // Keep garment filter in sync with avatar gender by default
  useEffect(() => {
    if (selectedAvatarGender === 'male' || selectedAvatarGender === 'female') {
      setGarmentGenderFilter(selectedAvatarGender);
    } else {
      setGarmentGenderFilter('all');
    }
    // We intentionally set on every avatar gender change so the default follows the avatar.
    // Users can still override manually afterward.
  }, [selectedAvatarGender]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    // Abort any in-flight request
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) {}
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setResult(null);
  setStatus('Preparing…');
  setProgress(0);

    const formData = new FormData();
    // Resolve avatar: file OR library pick
    if (avatarSource?.type === 'library' && avatarSource.url) {
      try {
        const resp = await fetch(avatarSource.url);
        const blob = await resp.blob();
        const libFile = new File([blob], avatarSource.name || 'avatar.svg', { type: blob.type || 'image/svg+xml' });
        formData.append('avatar', libFile);
      } catch (e2) {
        setError('Failed to load library avatar');
        setSubmitting(false);
        return;
      }
    } else {
      formData.append('avatar', avatar);
    }
    // Resolve garment (item): file OR library pick
    if (garmentSource?.type === 'library' && garmentSource.url) {
      try {
        const resp = await fetch(garmentSource.url);
        const blob = await resp.blob();
        const libFile = new File([blob], garmentSource.name || 'garment.jpg', { type: blob.type || 'image/jpeg' });
        formData.append('item', libFile);
      } catch (e2) {
        setError('Failed to load library garment');
        setSubmitting(false);
        return;
      }
    } else {
      formData.append('item', item);
    }
    formData.append('prompt', prompt);

    try {
  const base = API_BASE || window.location.origin;
  const url = new URL('/tryon/', base);
  const response = await fetch(url.toString(), {
        method: 'POST',
        body: formData,
    credentials: 'include',
        signal: controller.signal,
      });

      if (!response.ok) {
        let detail = 'Server error: ' + response.status;
        try {
            const errJson = await response.json();
            if (errJson?.detail) detail = errJson.detail;
        } catch (_) { /* ignore parse errors */ }
        setStatus('Failed');
        setError(detail);
        if (response.status === 403) {
          // Likely out of credits; refresh credits info
          fetchCredits();
        }
        return;
      }

  setStatus('Output is in progress…');

      let data;
      try {
        data = await response.json();
      } catch (err) {
        const text = await response.text();
        setStatus('Response parse error');
        setError('Error parsing response. Raw: ' + text.slice(0, 200));
        return;
      }

      if (data.result_image && typeof data.result_image === 'string') {
        setResult(data.result_image);
  setStatus('Status: Done');
  setProgress(100);
        if (typeof data.remaining_credits === 'number') {
          setCreditsInfo(ci => {
            const next = { ...ci, remaining: data.remaining_credits };
            broadcastCredits(next);
            return next;
          });
          // Notify other components (e.g., header) to refresh user info/credits
          try { window.dispatchEvent(new Event('auth-change')); } catch (_) {}
        }
        // Briefly enter a sync state so UI waits for refreshed credits
        setSyncingCredits(true);
        try {
          await fetchCredits();
        } catch (_) {
          // ignore; button will still re-enable
        } finally {
          setSyncingCredits(false);
        }
      } else {
  setStatus('Failed');
        setError(data.error || data.result_image?.error || 'Error: Could not generate image.');
      }
    } catch (err) {
      setStatus('Network error');
      setError('Network error: ' + err.message);
    }
    finally {
      setSubmitting(false);
      abortRef.current = null;
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      setError('Avatar file too large (max 10MB)');
      return;
    }
    setAvatar(file);
    setAvatarSource(file ? { type: 'file', name: file.name } : null);
    // When user uploads a custom avatar, allow all garments by default
    setGarmentGenderFilter('all');
    if (file && avatarPreviewRef.current) {
      const url = URL.createObjectURL(file);
      avatarPreviewRef.current.onload = () => URL.revokeObjectURL(url);
      avatarPreviewRef.current.src = url;
    }
  };

  const selectLibraryAvatar = (entry) => {
    const newGender = entry.gender || null;
    setAvatar(null);
    setAvatarSource({ type: 'library', name: entry.name, url: entry.file });
    if (avatarPreviewRef.current) avatarPreviewRef.current.src = entry.file;
    setAvatarLibOpen(false);
    // When selecting a library avatar, auto-apply garment gender filter to match avatar gender
    if (newGender === 'male' || newGender === 'female') {
      setGarmentGenderFilter(newGender);
    } else {
      setGarmentGenderFilter('all');
    }
    // Conditionally clear garment only if gender conflicts
    if (garmentSource?.type === 'library') {
      const g = garmentLibrary.find(gl => gl.file === garmentSource.url);
      const garmentGender = g?.gender || null;
      if (newGender && garmentGender && newGender !== garmentGender) {
        setItem(null);
        setGarmentSource(null);
        if (itemPreviewRef.current) itemPreviewRef.current.src = '';
      }
    }
  };

  const clearAvatarSelection = () => {
    setAvatar(null);
    setAvatarSource(null);
    // With no avatar selected, default garment filter to 'all'
    setGarmentGenderFilter('all');
    if (avatarPreviewRef.current) avatarPreviewRef.current.src = '';
    // Do not clear garment when simply clearing the avatar; allow user to keep the garment
  };

  const handleItemChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      setError('Item file too large (max 10MB)');
      return;
    }
    setItem(file);
    setGarmentSource(file ? { type: 'file', name: file.name } : null);
    if (file && itemPreviewRef.current) {
      const url = URL.createObjectURL(file);
      itemPreviewRef.current.onload = () => URL.revokeObjectURL(url);
      itemPreviewRef.current.src = url;
    }
  };

  const selectLibraryGarment = (entry) => {
    setItem(null);
    setGarmentSource({ type: 'library', name: entry.name, url: entry.file });
    if (itemPreviewRef.current) itemPreviewRef.current.src = entry.file;
    setGarmentLibOpen(false);
  };

  const clearGarmentSelection = () => {
    setItem(null);
    setGarmentSource(null);
    if (itemPreviewRef.current) itemPreviewRef.current.src = '';
    // Clear prompt since one of the inputs is now missing
    setPrompt('');
    setPromptEdited(false);
  };

  const isIndeterminate = status === 'Preparing…' || status.startsWith('Output is in progress');

  // Scroller helpers for garment library
  const updateScrollButtons = () => {
    const el = garmentScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = garmentScrollRef.current;
    if (!el) return;
    updateScrollButtons();
    const onScroll = () => updateScrollButtons();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateScrollButtons());
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garmentLibOpen]);

  const scrollGarmentsBy = (dir = 1) => {
    const el = garmentScrollRef.current;
    if (!el) return;
    const delta = Math.round(el.clientWidth * 0.8) * dir;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  // Build a same-origin proxy URL for the result to allow safe canvas conversion and downloads
  useEffect(() => {
    if (result) {
      try {
        const base = API_BASE || window.location.origin;
        const u = new URL('/image-proxy', base);
        u.searchParams.set('url', result);
        setProxyUrl(u.toString());
      } catch (_) {
        setProxyUrl(null);
      }
      setShareUrlCache(null); // reset cache when result changes
    } else {
      setProxyUrl(null);
      setShareUrlCache(null);
    }
  }, [result]);

  // Build a first-party URL by caching the image on the backend
  const buildFirstPartyShareUrl = async () => {
    if (!result) return null;
    let shareUrlFirstParty = result;
    if (shareUrlCache) return shareUrlCache;
    try {
  const base = API_BASE || window.location.origin;
  const cacheUrl = new URL('/cache-image', base);
  const resp = await fetch(cacheUrl.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: result })
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data?.share_path) {
          const base = API_BASE || window.location.origin;
          shareUrlFirstParty = `${base}${data.share_path}`;
        }
      }
    } catch (_) { /* ignore and fallback */ }
    setShareUrlCache(shareUrlFirstParty);
    return shareUrlFirstParty;
  };

  // Helper: load image via proxy to avoid CORS taint, draw to canvas, export
  const convertAndDownload = async (format = 'image/png') => {
    const src = proxyUrl || result;
    if (!src) return;
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const blob = await fetch(src).then(r => r.blob());
      const objUrl = URL.createObjectURL(blob);
      await new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = objUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(objUrl);
      const mime = format === 'jpg' || format === 'jpeg' ? 'image/jpeg' : format === 'png' ? 'image/png' : format;
      const dataUrl = canvas.toDataURL(mime, 0.92);
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `tryon-result.${mime === 'image/png' ? 'png' : 'jpg'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setError('Download failed: ' + (e?.message || e));
    }
  };

  const shareImage = async () => {
    try {
      if (!result) return;
      // First, create a first-party share URL to hide upstream URLs
      const shareUrlFirstParty = await buildFirstPartyShareUrl();
      // Prefer Web Share API with files if supported
      if (navigator.canShare && navigator.canShare({ files: [] })) {
        const src = proxyUrl || result;
        const resp = await fetch(src);
        const blob = await resp.blob();
        const file = new File([blob], 'tryon-result.png', { type: blob.type || 'image/png' });
        await navigator.share({
          files: [file],
          title: 'My Virtual Try-On',
          text: 'Check out this try-on result!',
        });
        return;
      }
      // If basic Web Share is available, share the URL
      if (navigator.share) {
        await navigator.share({
          title: 'My Virtual Try-On',
          text: 'Check out this try-on result!',
          url: shareUrlFirstParty,
        });
        return;
      }
      // Fallback: open share URLs in a new window
      const shareUrl = encodeURIComponent(shareUrlFirstParty);
      const text = encodeURIComponent('Check out my virtual try-on result!');
      // Open a chooser window with platform options
      const chooser = window.open('', '_blank', 'width=480,height=560,noopener');
      if (chooser) {
        chooser.document.write(`
          <html><head><title>Share</title></head>
          <body style="font-family: sans-serif; padding: 16px;">
            <h3 style="margin-top:0">Share your try-on</h3>
            <ul style="list-style:none; padding-left:0;">
              <li style="margin:8px 0;"><a href="https://twitter.com/intent/tweet?url=${shareUrl}&text=${text}" target="_blank" rel="noopener">Share on Twitter/X</a></li>
              <li style="margin:8px 0;"><a href="https://www.facebook.com/sharer/sharer.php?u=${shareUrl}" target="_blank" rel="noopener">Share on Facebook</a></li>
              <li style="margin:8px 0;"><a href="https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}" target="_blank" rel="noopener">Share on LinkedIn</a></li>
              <li style="margin:8px 0;"><a href="mailto:?subject=My%20Virtual%20Try-On&body=${text}%0A%0A${shareUrl}" target="_blank" rel="noopener">Share via Email</a></li>
              <li style="margin:8px 0;"><a href="${shareUrlFirstParty}" target="_blank" rel="noopener">Open image link</a></li>
            </ul>
          </body></html>
        `);
      } else {
        // If popup blocked, fallback to Twitter
        window.open(`https://twitter.com/intent/tweet?url=${shareUrl}&text=${text}`, '_blank', 'noopener');
      }
    } catch (e) {
      setError('Share failed: ' + (e?.message || e));
    }
  };

  const copyLink = async () => {
    try {
      if (!result) return;
      const url = await buildFirstPartyShareUrl();
      if (!url) throw new Error('No URL to copy');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const tmp = document.createElement('textarea');
        tmp.value = url;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
      }
      const oldStatus = status;
      setStatus('Link copied to clipboard');
      setTimeout(() => setStatus(oldStatus), 1500);
    } catch (e) {
      setError('Copy failed: ' + (e?.message || e));
    }
  };

  const useResultAsAvatar = async () => {
    try {
      if (!result) return;
      setError(null);
      const src = proxyUrl || result;
      const resp = await fetch(src);
      if (!resp.ok) throw new Error('Failed to fetch result image');
      const blob = await resp.blob();
      const file = new File([blob], 'result-as-avatar.jpg', { type: blob.type || 'image/jpeg' });
      setAvatar(file);
      setAvatarSource({ type: 'file', name: file.name });
      // File-based avatar -> allow all garments by default
      setGarmentGenderFilter('all');
      if (avatarPreviewRef.current) {
        const url = URL.createObjectURL(blob);
        avatarPreviewRef.current.onload = () => URL.revokeObjectURL(url);
        avatarPreviewRef.current.src = url;
      }
      // Optionally reset progress/status for clarity
      setStatus('Result adopted as new avatar');
      setTimeout(() => setStatus('Idle'), 1500);
    } catch (e) {
      setError('Could not reuse result as avatar: ' + (e?.message || e));
    }
  };

  const [promptEdited, setPromptEdited] = useState(false);
  const showOtpInline = !creditsInfo?.debug && !userInfo && typeof creditsInfo.remaining === 'number' && creditsInfo.remaining <= 0;

  useEffect(() => {
    if (showOtpInline) {
      // Auto-focus email or code based on step
      setTimeout(() => {
        try {
          if (!otpSent && otpEmailRef.current) otpEmailRef.current.focus();
          if (otpSent && otpCodeRef.current) otpCodeRef.current.focus();
        } catch (_) {}
      }, 50);
    }
  }, [showOtpInline, otpSent]);

  const requestOtp = async () => {
    try {
      setOtpError('');
      setOtpStatus('Sending OTP…');
      const base = API_BASE || window.location.origin;
      const url = new URL('/auth/request-otp', base);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: otpEmail })
      });
      if (!resp.ok) {
        const j = await resp.json().catch(()=>({}));
        throw new Error(j?.detail || `Server ${resp.status}`);
      }
      setOtpSent(true);
      setOtpStatus('OTP sent. Check your email.');
      setTimeout(() => { try { otpCodeRef.current?.focus(); } catch(_){} }, 25);
    } catch (e) {
      setOtpError(e.message || 'Failed to send OTP');
      setOtpStatus('');
    }
  };

  const verifyOtp = async () => {
    try {
      setOtpError('');
      setOtpStatus('Verifying…');
      const base = API_BASE || window.location.origin;
      const url = new URL('/auth/verify-otp', base);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: otpEmail, code: otpCode })
      });
      if (!resp.ok) {
        const j = await resp.json().catch(()=>({}));
        throw new Error(j?.detail || `Server ${resp.status}`);
      }
      setOtpStatus('Signed in');
      setOtpSent(false);
      setOtpCode('');
      setOtpEmail('');
      // Refresh credits and notify other components
      await fetchCredits();
      window.dispatchEvent(new Event('auth-change'));
      setTimeout(() => setOtpStatus(''), 1200);
    } catch (e) {
      setOtpError(e.message || 'Failed to verify OTP');
      setOtpStatus('');
    }
  };

  const loginWithPassword = async () => {
    try {
      setOtpError('');
      setOtpStatus('Signing in…');
      const base = API_BASE || window.location.origin;
      const url = new URL('/auth/login-password', base);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: otpEmail, password: pwd })
      });
      if (!resp.ok) {
        let j = {};
        try { j = await resp.json(); } catch(_) {}
        // Expose Forgot Password only when it's a wrong-password case
        if (resp.status === 401) setShowForgotAfterFail(true); else setShowForgotAfterFail(false);
        throw new Error(j?.detail || `Server ${resp.status}`);
      }
      setOtpStatus('Signed in');
      setPwd('');
      setShowForgotAfterFail(false);
      await fetchCredits();
      window.dispatchEvent(new Event('auth-change'));
      setTimeout(() => setOtpStatus(''), 1200);
    } catch (e) {
      setOtpError(e.message || 'Failed to sign in');
      setOtpStatus('');
    }
  };

  const requestResetCode = async () => {
    try {
      setOtpError('');
      setOtpStatus('Sending reset code…');
      const base = API_BASE || window.location.origin;
      const url = new URL('/auth/request-reset', base);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: otpEmail })
      });
      if (!resp.ok) {
        const j = await resp.json().catch(()=>({}));
        throw new Error(j?.detail || `Server ${resp.status}`);
      }
      setResetSent(true);
      setOtpStatus('Reset code sent');
      setTimeout(()=>{ try{ otpCodeRef.current?.focus(); }catch(_){} }, 25);
    } catch (e) {
      setOtpError(e.message || 'Failed to send reset code');
      setOtpStatus('');
    }
  };

  const verifyReset = async () => {
    try {
      setOtpError('');
      setOtpStatus('Resetting…');
      const base = API_BASE || window.location.origin;
      const url = new URL('/auth/verify-reset', base);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: otpEmail, code: otpCode, new_password: newPwd })
      });
      if (!resp.ok) {
        const j = await resp.json().catch(()=>({}));
        throw new Error(j?.detail || `Server ${resp.status}`);
      }
      setOtpStatus('Password updated');
      setResetSent(false);
      setOtpCode('');
      setNewPwd('');
      await fetchCredits();
      window.dispatchEvent(new Event('auth-change'));
      setTimeout(() => setOtpStatus(''), 1200);
    } catch (e) {
      setOtpError(e.message || 'Failed to reset password');
      setOtpStatus('');
    }
  };

  const extractKeywordsFromName = (name='') => {
    const base = name.toLowerCase().replace(/\.[a-z0-9]+$/, '');
    const tokens = base.split(/[^a-z0-9]+/).filter(Boolean);
    // simple allowlist of fashion-y tokens
    const keep = tokens.filter(t => t.length > 2 && !['avatar','garment','image','photo','file','result','as','the','with','and'].includes(t));
    return [...new Set(keep)].slice(0,4);
  };

  const classifyGarment = (keywords=[]) => {
    const kset = new Set(keywords);
    const has = (...cands) => cands.some(c => kset.has(c));
    if (has('saree','sari','saree1','pallu')) return { cat: 'saree', line: 'Traditional saree draping with realistic pleats and flowing pallu' };
    if (has('lehenga')) return { cat: 'lehenga', line: 'Festive lehenga outfit with authentic skirt volume and blouse fit' };
    if (has('blouse')) return { cat: 'blouse', line: 'Well-fitted blouse with realistic fabric folds at shoulders' };
    if (has('sunglasses','glasses','shades','eyewear')) return { cat: 'sunglasses', line: 'Stylish sunglasses aligned naturally on the face with correct reflections' };
    if (has('tshirt','tee','t-shirt')) return { cat: 'tshirt', line: 'Casual fitted t-shirt with natural fabric creases' };
    if (has('shirt','buttondown','button-up')) return { cat: 'shirt', line: 'Button-up shirt with realistic collar, buttons and sleeve folds' };
    if (has('jacket','blazer','coat')) return { cat: 'jacket', line: 'Tailored outer layer with natural shoulder structure and lapels' };
    if (has('dress','gown')) return { cat: 'dress', line: 'Dress with realistic drape, waist shaping and fabric flow' };
    if (has('skirt')) return { cat: 'skirt', line: 'Skirt with natural flare and shadowing' };
    if (has('jeans','denim','pants','trousers')) return { cat: 'pants', line: 'Well-fitted lower garment with natural folds at knees and hips' };
    if (has('shoes','sneakers','heels','boot','boots')) return { cat: 'footwear', line: 'Footwear aligned correctly with realistic perspective and shadows' };
    if (has('handbag','bag','purse','clutch')) return { cat: 'accessory-bag', line: 'Accessory bag with believable strap tension and soft shadow' };
    if (has('hat','cap')) return { cat: 'headwear', line: 'Headwear seated naturally with correct perspective and shadow line' };
    if (has('scarf','dupatta','shawl','stole')) return { cat: 'scarf', line: 'Flowing wrap accessory with layered folds and soft edges' };
    return { cat: 'garment', line: 'Selected garment rendered with correct fit and natural fabric behavior' };
  };

  // Color detection removed per user preference; focusing on garment category semantics

  const buildSuggestedPrompt = async () => {
    const parts = [];
    // Avatar context
    parts.push('Realistic full-body fashion visualization');
    // Garment
    let garmentKeywords = [];
    if (garmentSource?.type === 'library') garmentKeywords = extractKeywordsFromName(garmentSource.name || garmentSource.url || '');
    else if (item) garmentKeywords = extractKeywordsFromName(item.name || '');
  const { line } = classifyGarment(garmentKeywords);
  parts.push(line);
    // Quality / style line
    parts.push('High detail, correct body proportions, realistic fabric texture, soft lighting');
    return parts.slice(0,3).join('\n');
  };

  // Auto-suggest when garment or avatar changes if user has not manually edited after last suggestion
  useEffect(()=>{
    const gen = async () => {
      const hasGarment = !!(garmentSource || item);
      const hasAvatar = !!(avatarSource || avatar);
      if (!promptEdited && hasGarment && hasAvatar) {
        const suggestion = await buildSuggestedPrompt();
        setPrompt(suggestion);
      }
    };
    gen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garmentSource, item, avatarSource, avatar]);

  // Keep prompt empty if either avatar or garment missing
  useEffect(() => {
    const hasGarment = !!(garmentSource || item);
    const hasAvatar = !!(avatarSource || avatar);
    if (!hasGarment || !hasAvatar) {
      if (prompt !== '') setPrompt('');
      if (promptEdited) setPromptEdited(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garmentSource, item, avatarSource, avatar]);

  const onPromptChange = (e) => {
    setPrompt(e.target.value);
    setPromptEdited(true);
  };

  const regenerateSuggestion = async () => {
    const suggestion = await buildSuggestedPrompt();
    setPrompt(suggestion);
    setPromptEdited(false); // allow further auto-updates if garment changes again
  };

  // (Removed unused clearAll helper to avoid ESLint no-unused-vars warning)

  return (
    <div className="tryon-container">
    <div className="tryon-left">
  <h2>AI Outfit Planner</h2>
        <div className="tryon-form">
          <div className="form-group">
            <label>Upload Avatar Photo</label>
            {/* Hidden inputs: one forces Camera, one opens Gallery/File picker */}
            <input ref={avatarCamInputRef} type="file" onChange={handleAvatarChange} accept="image/*" capture="environment" style={{ display:'none' }} />
            <input ref={avatarFileInputRef} type="file" onChange={handleAvatarChange} accept="image/*" style={{ display:'none' }} />
            {isMobile ? (
              <>
                <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                  <button type="button" className="soft-blue-btn" onClick={() => avatarCamInputRef.current && avatarCamInputRef.current.click()} style={{ fontSize:'0.75rem' }}>
                    Use Camera
                  </button>
                  <button type="button" className="soft-blue-btn" onClick={() => avatarFileInputRef.current && avatarFileInputRef.current.click()} style={{ fontSize:'0.75rem' }}>
                    Choose from Gallery
                  </button>
                </div>
                <div style={{ fontSize:'0.7rem', color:'#6b7280', marginTop:'0.25rem' }}>Tip: You can choose Camera or Gallery.</div>
              </>
            ) : (
              <button type="button" className="soft-blue-btn" onClick={() => avatarFileInputRef.current && avatarFileInputRef.current.click()} style={{ fontSize:'0.75rem' }}>
                Upload Your Avatar
              </button>
            )}
            <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.35rem', flexWrap:'wrap', alignItems:'center' }}>
              <button type="button" className="soft-blue-btn" onClick={() => setAvatarLibOpen(v => !v)} style={{ fontSize:'0.75rem' }}>
                {avatarLibOpen ? 'Hide Default Avatars' : 'Use Default Avatars'}
              </button>
              {(avatar || avatarSource) && (
                <div style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem' }}>
                  <button type="button" className="soft-blue-btn" onClick={clearAvatarSelection} style={{ fontSize:'0.75rem' }}>Clear</button>
                  {avatarSource?.type === 'library' && (
                    <span style={{ fontSize:'0.7rem', opacity:0.75 }}>Selected: {avatarSource.name}</span>
                  )}
                </div>
              )}
            </div>
            {avatarLibOpen && (
              <div style={{ marginTop:'0.5rem', border:'1px solid #ddd', padding:'0.5rem', borderRadius:4 }}>
                <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.4rem', flexWrap:'wrap', alignItems:'center' }}>
                  <span style={{ fontSize:'0.65rem', opacity:0.7 }}>Filter:</span>
                  <button type="button" style={{ fontSize:'0.6rem', padding:'0.15rem 0.4rem', background: avatarGenderFilter==='all' ? '#4a6fa5' : '#eee', color: avatarGenderFilter==='all' ? '#fff' : '#222', border:'1px solid #999', borderRadius:3 }} onClick={() => setAvatarGenderFilter('all')}>All</button>
                  <button type="button" style={{ fontSize:'0.6rem', padding:'0.15rem 0.4rem', background: avatarGenderFilter==='male' ? '#4a6fa5' : '#eee', color: avatarGenderFilter==='male' ? '#fff' : '#222', border:'1px solid #999', borderRadius:3 }} onClick={() => setAvatarGenderFilter('male')}>Male</button>
                  <button type="button" style={{ fontSize:'0.6rem', padding:'0.15rem 0.4rem', background: avatarGenderFilter==='female' ? '#4a6fa5' : '#eee', color: avatarGenderFilter==='female' ? '#fff' : '#222', border:'1px solid #999', borderRadius:3 }} onClick={() => setAvatarGenderFilter('female')}>Female</button>
                </div>
                <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap' }}>
                  {avatarLibrary.filter(a => avatarGenderFilter === 'all' || a.gender === avatarGenderFilter).map(a => (
                    <div key={a.file} style={{ cursor:'pointer', textAlign:'center' }} onClick={() => selectLibraryAvatar(a)}>
                      <img
                        src={a.file}
                        alt={a.name}
                        style={{ width:64, height:64, objectFit:'cover', border: avatarSource?.url === a.file ? '2px solid #4a6fa5' : '1px solid #999', borderRadius:4 }}
                      />
                      <div style={{ fontSize:'0.6rem', marginTop:2 }}>{a.name}</div>
                    </div>
                  ))}
                  {avatarLibrary.filter(a => avatarGenderFilter === 'all' || a.gender === avatarGenderFilter).length === 0 && (
                    <div style={{ fontSize:'0.6rem', opacity:0.6 }}>No avatars for this filter</div>
                  )}
                </div>
              </div>
            )}
            <div className="preview-box">
              {!avatar && !avatarSource && <span className="placeholder-text">Avatar preview</span>}
              <img
                ref={avatarPreviewRef}
                className="preview-img"
                alt="Avatar preview"
                style={{ display: (avatar || avatarSource) ? 'block' : 'none' }}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Upload Garment/Item Photo</label>
            {/* Hidden inputs: one forces Camera, one opens Gallery/File picker */}
            <input ref={garmentCamInputRef} type="file" onChange={handleItemChange} accept="image/*" capture="environment" style={{ display:'none' }} />
            <input ref={garmentFileInputRef} type="file" onChange={handleItemChange} accept="image/*" style={{ display:'none' }} />
            {isMobile ? (
              <>
                <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                  <button type="button" className="soft-blue-btn" onClick={() => garmentCamInputRef.current && garmentCamInputRef.current.click()} style={{ fontSize:'0.75rem' }}>
                    Use Camera
                  </button>
                  <button type="button" className="soft-blue-btn" onClick={() => garmentFileInputRef.current && garmentFileInputRef.current.click()} style={{ fontSize:'0.75rem' }}>
                    Choose from Gallery
                  </button>
                </div>
                <div style={{ fontSize:'0.7rem', color:'#6b7280', marginTop:'0.25rem' }}>Tip: You can choose Camera or Gallery.</div>
              </>
            ) : (
              <button type="button" className="soft-blue-btn" onClick={() => garmentFileInputRef.current && garmentFileInputRef.current.click()} style={{ fontSize:'0.75rem' }}>
                Upload Your Garment
              </button>
            )}
            <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.35rem', flexWrap:'wrap', alignItems:'center' }}>
              <button type="button" className="soft-blue-btn" onClick={() => setGarmentLibOpen(v => !v)} style={{ fontSize:'0.75rem' }}>
                {garmentLibOpen ? 'Hide Garment Library' : 'Use Garment Library'}
              </button>
              {(item || garmentSource) && (
                <div style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem' }}>
                  <button type="button" className="soft-blue-btn" onClick={clearGarmentSelection} style={{ fontSize:'0.75rem' }}>Clear</button>
                  {garmentSource?.type === 'library' && (
                    <span style={{ fontSize:'0.7rem', opacity:0.75 }}>Selected: {garmentSource.name}</span>
                  )}
                </div>
              )}
            </div>
            {garmentLibOpen && (
              <div style={{ marginTop:'0.5rem', border:'1px solid #ddd', padding:'0.5rem', borderRadius:4, position:'relative' }}>
                <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.4rem', flexWrap:'wrap', alignItems:'center' }}>
                  <span style={{ fontSize:'0.65rem', opacity:0.7 }}>Filter:</span>
                  <button type="button" style={{ fontSize:'0.6rem', padding:'0.15rem 0.4rem', background: garmentGenderFilter==='all' ? '#4a6fa5' : '#eee', color: garmentGenderFilter==='all' ? '#fff' : '#222', border:'1px solid #999', borderRadius:3 }} onClick={() => setGarmentGenderFilter('all')}>All</button>
                  <button type="button" style={{ fontSize:'0.6rem', padding:'0.15rem 0.4rem', background: garmentGenderFilter==='male' ? '#4a6fa5' : '#eee', color: garmentGenderFilter==='male' ? '#fff' : '#222', border:'1px solid #999', borderRadius:3 }} onClick={() => setGarmentGenderFilter('male')}>Male</button>
                  <button type="button" style={{ fontSize:'0.6rem', padding:'0.15rem 0.4rem', background: garmentGenderFilter==='female' ? '#4a6fa5' : '#eee', color: garmentGenderFilter==='female' ? '#fff' : '#222', border:'1px solid #999', borderRadius:3 }} onClick={() => setGarmentGenderFilter('female')}>Female</button>
                </div>
                <div className="scroller-shell">
                  <div className={`scroller-gradient left ${canScrollLeft ? 'show' : ''}`} />
                  <div ref={garmentScrollRef} className="garment-scroller" role="listbox" aria-label="Garment library">
                    <div className="garment-row">
                      {garmentLibrary
                        .filter(g => (!selectedAvatarGender || g.gender === selectedAvatarGender) && (garmentGenderFilter === 'all' || g.gender === garmentGenderFilter))
                        .map(g => (
                          <div key={g.file} className="garment-tile" onClick={() => selectLibraryGarment(g)} role="option" aria-selected={garmentSource?.url === g.file}>
                            <div className={`garment-thumb ${garmentSource?.url === g.file ? 'selected' : ''}`}>
                              <img src={g.file} alt={g.name} />
                            </div>
                            <div className="garment-name" title={g.name}>{g.name}</div>
                          </div>
                        ))}
                      {garmentLibrary.filter(g => (!selectedAvatarGender || g.gender === selectedAvatarGender) && (garmentGenderFilter === 'all' || g.gender === garmentGenderFilter)).length === 0 && (
                        <div style={{ fontSize:'0.6rem', opacity:0.6, padding:'0.25rem 0.5rem' }}>No garments for this filter</div>
                      )}
                    </div>
                  </div>
                  <div className={`scroller-gradient right ${canScrollRight ? 'show' : ''}`} />
                  <button type="button" className={`scroller-btn left ${!canScrollLeft ? 'disabled' : ''}`} onClick={() => scrollGarmentsBy(-1)} aria-label="Scroll left">‹</button>
                  <button type="button" className={`scroller-btn right ${!canScrollRight ? 'disabled' : ''}`} onClick={() => scrollGarmentsBy(1)} aria-label="Scroll right">›</button>
                </div>
              </div>
            )}
            <div className="preview-box garment-preview">
              {!item && !garmentSource && <span className="placeholder-text">Garment preview</span>}
              <img
                ref={itemPreviewRef}
                className="preview-img"
                alt="Garment preview"
                style={{ display: (item || garmentSource) ? 'block' : 'none' }}
              />
            </div>
          </div>
        </div>
      </div>

    <div className="tryon-right">
  <h2>Automated Prompt</h2>
        <div className="form-group">
          <label style={{display:'none'}}>Prompt</label>
          <textarea
            value={
              isDebug
                ? (prompt || '')
                : (() => {
                    const p = (prompt || '').trim();
                    if (!p) return '';
                    const first = p.split('\n')[0] || '';
                    return `${first}\n[.......hidden]`;
                  })()
            }
            onChange={isDebug ? onPromptChange : undefined}
            placeholder={isDebug ? 'Describe the style or result' : ''}
            rows={isDebug ? 4 : 2}
            disabled={!isDebug}
            style={{ width: '100%', resize: isDebug ? 'vertical' : 'none', overflow: 'hidden' }}
          />
          <div className="compact-buttons" style={{marginTop:'0.3rem', display:'flex', gap:'0.4rem', flexWrap:'wrap'}}>
            {isDebug && (
              <button type="button" onClick={regenerateSuggestion}>Regenerate Suggestion</button>
            )}
            {promptEdited && <span style={{fontSize:'0.6rem', opacity:0.65, alignSelf:'center'}}>Edited</span>}
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={
            submitting ||
            syncingCredits ||
            !(avatar || avatarSource) ||
            !(item || garmentSource) ||
            (!creditsInfo?.debug && !userInfo && typeof creditsInfo.remaining === 'number' && creditsInfo.remaining <= 0)
          }
          className="primary-action"
        >
          {submitting ? 'Working…' : 'Click To Try-On The Garment'}
        </button>
        {/* Credits indicator directly below CTA */}
        <div style={{ marginTop: '0.5rem', fontSize: '1rem', color: '#8B4513' }}>
          {creditsInfo?.debug ? (
            <span>Free image credits remaining: <strong>∞</strong> (debug)</span>
          ) : syncingCredits ? (
            <span>Syncing credits…</span>
          ) : userInfo && typeof userInfo.free_credits === 'number' ? (
            <span>Free image credits remaining: <strong>{userInfo.free_credits}</strong></span>
          ) : typeof creditsInfo.remaining === 'number' ? (
            <span>Free image credits remaining: <strong>{creditsInfo.remaining}</strong></span>
          ) : (
            <span>Syncing credits…</span>
          )}
        </div>
        {showOtpInline && (
          <div style={{
            marginTop: '0.5rem',
            padding: '0.5rem',
            border: '1px dashed #b57f50',
            borderRadius: 6,
            background: '#fffaf6'
          }}>
            <div style={{ fontSize: '0.95rem', marginBottom: '0.25rem', color: '#8B4513' }}>
              Sign in to continue with your account credits
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
              Account credits are linked to your email ID across devices.
            </div>
            <div style={{ fontSize:'0.8rem', marginBottom:'0.35rem', display:'flex', gap:'0.75rem', flexWrap:'wrap' }}>
              <button type="button" className="mini-btn" onClick={() => { setAuthMode('otp'); setOtpError(''); setOtpStatus(''); }} aria-pressed={authMode==='otp'}>Use OTP</button>
              <button type="button" className="mini-btn" onClick={() => { setAuthMode('password'); setOtpError(''); setOtpStatus(''); }} aria-pressed={authMode==='password'}>Use Password</button>
              {(showForgotAfterFail || authMode==='reset') && (
                <button type="button" className="mini-btn" onClick={() => { setAuthMode('reset'); setOtpError(''); setOtpStatus(''); setResetSent(false); }} aria-pressed={authMode==='reset'}>Forgot Password</button>
              )}
            </div>
            {authMode === 'otp' && (!otpSent ? (
              <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
                <input
                  ref={otpEmailRef}
                  type="email"
                  placeholder="Enter your email"
                  value={otpEmail}
                  onChange={e => setOtpEmail(e.target.value)}
                  style={{ padding:'6px 8px', minWidth: 220 }}
                />
                <button
                  type="button"
                  onClick={requestOtp}
                  disabled={!otpEmail.includes('@')}
                  style={{
                    background:'#c62828', color:'#fff', border:'1px solid #a11',
                    padding:'6px 10px', borderRadius:4
                  }}
                >Get OTP</button>
                <span style={{ fontSize:'0.8rem' }}>
                  {otpStatus && <span style={{ color:'#2b6' }}>{otpStatus}</span>}
                  {otpError && <span style={{ color:'#b22', marginLeft:8 }}>{otpError}</span>}
                </span>
              </div>
            ) : (
              <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
                <input
                  ref={otpCodeRef}
                  type="text"
                  placeholder="Enter code"
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value)}
                  style={{ padding:'6px 8px', width: 140 }}
                />
                <button type="button" onClick={verifyOtp} disabled={otpCode.trim().length < 4}>Verify</button>
                <button type="button" onClick={()=>{ setOtpSent(false); setOtpCode(''); setOtpStatus(''); setOtpError(''); }}>Back</button>
                <span style={{ fontSize:'0.8rem' }}>
                  {otpStatus && <span style={{ color:'#2b6' }}>{otpStatus}</span>}
                  {otpError && <span style={{ color:'#b22', marginLeft:8 }}>{otpError}</span>}
                </span>
              </div>
            ))}
            {authMode === 'password' && (
              <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={otpEmail}
                  onChange={e => setOtpEmail(e.target.value)}
                  style={{ padding:'6px 8px', minWidth: 220 }}
                />
                <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Password"
                    value={pwd}
                    onChange={e => setPwd(e.target.value)}
                    style={{ padding:'6px 8px', minWidth: 150 }}
                  />
                  <button
                    type="button"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    title={showPwd ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPwd(v => !v)}
                    className="mini-btn"
                    style={{ padding:'0.3rem 0.45rem', display:'flex', alignItems:'center', justifyContent:'center' }}
                  >
                    {showPwd ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </button>
                </span>
                <button type="button" onClick={loginWithPassword} disabled={!otpEmail.includes('@') || pwd.length < 1}>Sign In</button>
                <span style={{ fontSize:'0.8rem' }}>
                  {otpStatus && <span style={{ color:'#2b6' }}>{otpStatus}</span>}
                  {otpError && <span style={{ color:'#b22', marginLeft:8 }}>{otpError}</span>}
                </span>
              </div>
            )}
            {authMode === 'reset' && (!resetSent ? (
              <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={otpEmail}
                  onChange={e => setOtpEmail(e.target.value)}
                  style={{ padding:'6px 8px', minWidth: 220 }}
                />
                <button type="button" onClick={requestResetCode} disabled={!otpEmail.includes('@')}>Send Reset Code</button>
                <span style={{ fontSize:'0.8rem' }}>
                  {otpStatus && <span style={{ color:'#2b6' }}>{otpStatus}</span>}
                  {otpError && <span style={{ color:'#b22', marginLeft:8 }}>{otpError}</span>}
                </span>
              </div>
            ) : (
              <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
                <input
                  ref={otpCodeRef}
                  type="text"
                  placeholder="Reset code"
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value)}
                  style={{ padding:'6px 8px', width: 140 }}
                />
                <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}>
                  <input
                    type={showNewPwd ? 'text' : 'password'}
                    placeholder="New password (min 8)"
                    value={newPwd}
                    onChange={e => setNewPwd(e.target.value)}
                    style={{ padding:'6px 8px', minWidth: 160 }}
                  />
                  <button
                    type="button"
                    aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                    title={showNewPwd ? 'Hide password' : 'Show password'}
                    onClick={() => setShowNewPwd(v => !v)}
                    className="mini-btn"
                    style={{ padding:'0.3rem 0.45rem', display:'flex', alignItems:'center', justifyContent:'center' }}
                  >
                    {showNewPwd ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </button>
                </span>
                <button type="button" onClick={verifyReset} disabled={otpCode.trim().length < 4 || newPwd.length < 8}>Reset Password</button>
                <button type="button" onClick={()=>{ setResetSent(false); setOtpCode(''); setNewPwd(''); setOtpError(''); setOtpStatus(''); }}>Back</button>
                <span style={{ fontSize:'0.8rem' }}>
                  {otpStatus && <span style={{ color:'#2b6' }}>{otpStatus}</span>}
                  {otpError && <span style={{ color:'#b22', marginLeft:8 }}>{otpError}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
        {(!creditsInfo?.debug && creditsInfo.isUser && typeof creditsInfo.remaining === 'number' && creditsInfo.remaining <= 0) && (
          <div style={{ marginTop:'0.5rem', fontSize:'0.9rem', color:'#8B4513', fontFamily:'inherit' }}>
            You have used all your account credits. Please contact us to add more: 
            <a href="https://drive-tech-ai.com/articles/contact/" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, color: '#1d4ed8', textDecoration: 'none' }}>Contact Page</a>.
          </div>
        )}
        <h2 style={{marginTop: '1rem'}}>Output Image</h2>
  <div className="status" style={{ marginBottom: '1.25rem' }}>
          <div className="progress-bar">
            <div
              className={`progress ${isIndeterminate ? 'indeterminate' : ''}`}
              style={{ width: isIndeterminate ? '100%' : `${progress}%` }}
            />
          </div>
          <div className="status-text">{
            `Output Image Status: ${String(status || '').replace(/^Status:\s*/,'')}`
          }</div>
        </div>
        {!result && (
          <div className="info-box">
            <h2>Instructions To Use AI Outfit Planner [V1]</h2>
            <ul>
              <li>Upload a clear front-facing avatar and a single item photo (max 10 MB each) for best results.</li>
              <li>Select matching genders for avatar and garment to avoid misfits.</li>
              <li>Let the automated prompt generate after both images; you can simply press Click To Try-On The Garment and wait for the magic to happen.</li>
              <li>Free trial credits are included per IP. When they run out, sign in below the button to get more free credits.</li>
              <li>This tool is designed for ethical use only—avoid any harmful content.</li>
              <li>Possible use cases: outfit previews, catalog mockups, social looks, branding, promotional activities.</li>
              <li>Contact for inquiry or customization.</li>
            </ul>
          </div>
        )}
        
        {result && (
          <div className="result">
            <div className="result-wrapper" onClick={()=>setLightboxOpen(true)}>
              <img className="output-img" src={result} alt="Result" />
            </div>
            <div className="result-actions compact-buttons" style={{ marginTop: '0.5rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => convertAndDownload('png')}>PNG</button>
              <button type="button" onClick={() => convertAndDownload('jpg')}>JPG</button>
              <button type="button" onClick={copyLink}>Copy</button>
              <button type="button" onClick={shareImage}>Share</button>
              <button type="button" onClick={useResultAsAvatar}>Use as Avatar</button>
            </div>
          </div>
        )}
        {lightboxOpen && result && (
          <div className="lightbox" onClick={()=>setLightboxOpen(false)}>
            <div className="lightbox-inner">
              <img src={result} alt="Zoomed result" />
            </div>
          </div>
        )}
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}

export default TryOnForm;
